import { defer, from, Observable, shareReplay, throwError } from "rxjs";
import { catchError, finalize, switchMap } from "rxjs/operators";
import { LOCALSTORAGE_KEY } from "@config";
import type { HttpErrorShape, HttpPlugin, HttpRequestConfig } from "@kalodata/http";
import { isInIframe } from "@utils/window";
import { requestRefreshAccessToken } from "./refreshTokenRequest";
import { markHttpErrorToastShown } from "./httpErrorToast";

const defaultAttachToken = <TMeta, TContext>(
  request: HttpRequestConfig<unknown, TMeta, TContext>,
  token: string,
): HttpRequestConfig<unknown, TMeta, TContext> => {
  return {
    ...request,
    headers: {
      ...request.headers,
      Authorization: `Bearer ${token}`,
    },
  };
};

const defaultShouldRefresh = <TMeta, TContext>(error: HttpErrorShape<unknown, TMeta, TContext>) => {
  return error.status === 401 && (error.response?.data as { code?: string } | undefined)?.code === "TOKEN_EXPIRED";
};

const getToken = () => localStorage.getItem(LOCALSTORAGE_KEY.ACCESS_TOKEN);

const resolveRefreshToken = async () => {
  const accessToken = await requestRefreshAccessToken();
  if (accessToken) {
    localStorage.setItem(LOCALSTORAGE_KEY.ACCESS_TOKEN, accessToken);
  }
  return accessToken;
};

const redirectToLogin = () => {
  // 在 iframe 内鉴权失败时，通知父窗口登出而非自行跳转，避免把内嵌 iframe 自身导航走。
  // referrer 不可用时使用 "*"，LOGOUT 消息体无敏感载荷，接收方按 type 自行过滤。
  if (isInIframe()) {
    const parentOrigin = document.referrer ? new URL(document.referrer).origin : "*";
    window.parent.postMessage({ type: "LOGOUT" }, parentOrigin);
    return;
  }
  if (["/login", "/signup", "/authorization", "/privacy-policy"].includes(window.location.pathname)) return;
  const needSignUp = !localStorage.getItem("userName");
  const redirectUrl = encodeURIComponent(window.location.pathname + window.location.search);
  window.location.href = needSignUp ? `/signup?redirectUrl=${redirectUrl}` : `/login?redirectUrl=${redirectUrl}`;
};

export const createAuthPlugin = <TMeta = unknown, TContext = unknown>(): HttpPlugin<TMeta, TContext> => {
  let refreshingRequest$: Observable<string | undefined | null> | null = null;

  const getRefreshingRequest$ = (): Observable<string | undefined | null> => {
    if (refreshingRequest$) return refreshingRequest$;
    refreshingRequest$ = defer(() => from(resolveRefreshToken())).pipe(
      finalize(() => {
        refreshingRequest$ = null;
      }),
      shareReplay(1),
    );
    return refreshingRequest$;
  };

  const handleAuthFailed = (error: HttpErrorShape<unknown, TMeta, TContext>) => {
    // 跳转登录 / 通知父窗口登出属于「已处理」的鉴权失败，标记后避免 toast 插件再弹一个错误提示。
    markHttpErrorToastShown(error);
    redirectToLogin();
    return throwError(() => error);
  };

  return {
    name: "auth",
    onRequest: async (request) => {
      const token = getToken();
      if (!token) return request;
      return defaultAttachToken(request, token);
    },
    onError: (error, context) => {
      if (!defaultShouldRefresh(error)) {
        if (error.status === 401) {
          return handleAuthFailed(error);
        }
        throw error;
      }
      return getRefreshingRequest$().pipe(
        switchMap((token) => {
          if (!token) {
            return handleAuthFailed(error);
          }
          return context.retry(defaultAttachToken(context.request, token));
        }),
        catchError(handleAuthFailed),
      );
    },
  };
};
